import { z } from '@hono/zod-openapi'

export const EmailAddressSchema = z.string().email().openapi({ example: 'parent@example.com' })

export const EmailSchema = z.object({
  to: z.array(EmailAddressSchema).min(1).max(50).openapi({ description: 'Primary recipients' }),
  cc: z.array(EmailAddressSchema).max(50).nullable(),
  bcc: z.array(EmailAddressSchema).max(50).nullable(),
  from: EmailAddressSchema,
  replyTo: EmailAddressSchema.nullable(),
  subject: z.string().min(1).max(998).openapi({ example: 'IEP Meeting Scheduled' }),
  body: z.string().min(1).openapi({ example: 'Dear Parent, your IEP meeting is scheduled for...' }),
  bodyFormat: z.enum(['text', 'html']).default('text').openapi({ example: 'html' }),
  attachments: z.array(z.object({
    filename: z.string().max(255),
    contentType: z.string().openapi({ example: 'application/pdf' }),
    content: z.string().openapi({ description: 'Base64-encoded content' }),
  })).max(20).nullable(),
  headers: z.record(z.string()).nullable().openapi({ description: 'Custom email headers' }),
}).openapi('Email', { description: 'Email message payload' })

export const SmsSchema = z.object({
  to: z.string().min(1).max(20).openapi({ example: '+12065551234' }),
  from: z.string().min(1).max(20).nullable().openapi({ example: '+12065550000' }),
  body: z.string().min(1).max(1600).openapi({ example: 'Reminder: IEP meeting tomorrow at 2pm.' }),
  providerRef: z.string().nullable().openapi({ description: 'Twilio message SID' }),
}).openapi('Sms', { description: 'SMS message payload' })

export const PushNotificationSchema = z.object({
  title: z.string().min(1).max(255).openapi({ example: 'New Form Available' }),
  body: z.string().min(1).max(1000).openapi({ example: 'A consent form is ready for your signature.' }),
  targetUserId: z.string().uuid(),
  targetDeviceTokens: z.array(z.string()).max(100).nullable(),
  data: z.record(z.unknown()).nullable().openapi({ description: 'Arbitrary payload sent with notification' }),
  actions: z.array(z.object({
    action: z.string().openapi({ example: 'open-form' }),
    title: z.string().openapi({ example: 'Open Form' }),
    icon: z.string().nullable(),
  })).max(5).nullable(),
  priority: z.enum(['high', 'normal', 'low']).default('normal'),
  ttl: z.number().int().max(86400).nullable().openapi({ description: 'Time-to-live in seconds' }),
}).openapi('PushNotification', { description: 'Push notification payload' })

export const CalendarEventSchema = z.object({
  title: z.string().min(1).max(255).openapi({ example: 'IEP Annual Review' }),
  description: z.string().max(5000).nullable().openapi({ example: 'Annual review meeting for Jane Doe' }),
  start: z.string().datetime().openapi({ example: '2026-08-15T14:00:00Z' }),
  end: z.string().datetime().openapi({ example: '2026-08-15T15:00:00Z' }),
  timeZone: z.string().openapi({ example: 'America/Los_Angeles' }),
  location: z.string().max(500).nullable().openapi({ example: 'Room 204, Washington Elementary' }),
  isOnline: z.boolean().nullable(),
  onlineMeetingUrl: z.string().url().nullable().openapi({ example: 'https://meet.google.com/abc-defg-hij' }),
  attendees: z.array(z.object({
    email: EmailAddressSchema,
    name: z.string().max(255).nullable(),
    response: z.enum(['accepted', 'declined', 'tentative', 'needsAction']).nullable(),
  })).min(1).max(200),
  attachments: z.array(z.object({
    filename: z.string().max(255),
    fileUrl: z.string().url(),
  })).max(10).nullable(),
  reminders: z.array(z.object({
    minutes: z.number().int().openapi({ example: 30 }),
    method: z.enum(['email', 'popup']),
  })).nullable(),
}).openapi('CalendarEvent', { description: 'Calendar event payload' })

export const CommunicationDispatchSchema = z.object({
  id: z.string().uuid(),
  districtId: z.string().min(1).max(64),
  channel: z.enum(['email', 'sms', 'push', 'calendar']),
  status: z.enum(['pending', 'sent', 'delivered', 'failed', 'bounced']),
  provider: z.string().openapi({ example: 'sendgrid' }),
  providerRef: z.string().nullable().openapi({ description: 'Provider-side identifier' }),
  recipientId: z.string().uuid().nullable(),
  studentId: z.string().uuid().nullable(),
  subject: z.string().max(998).nullable(),
  errorMessage: z.string().nullable(),
  sentAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
}).openapi('CommunicationDispatch', { description: 'Outgoing communication record' })

export type Email = z.infer<typeof EmailSchema>
export type Sms = z.infer<typeof SmsSchema>
export type PushNotification = z.infer<typeof PushNotificationSchema>
export type CalendarEvent = z.infer<typeof CalendarEventSchema>
export type CommunicationDispatch = z.infer<typeof CommunicationDispatchSchema>
